import {ComplexStyleRule, StyleRule} from '@vanilla-extract/css';

export const screenSizes = {
  medium: 768,
  large: 1024,
} as const;

export const breakpoints = {
  medium: `@media (min-width: ${screenSizes.medium}px)`,
  large: `@media (min-width: ${screenSizes.large}px)`,
};

export type BreakpointName = 'small' | keyof typeof screenSizes;

export const outerGapWidth: Record<BreakpointName, number> = {
  small: 24,
  medium: 32,
  large: 32,
};

export const innerGapWidth: Record<BreakpointName, number> = {
  small: 24,
  medium: 24,
  large: 24,
};

/**
 * The overall content max width for the given breakpoint, including the inner
 * gaps between columns as well as the two outer gaps on either side.
 */
export const maxContentWidth: Record<BreakpointName, number> = {
  small: 544,
  medium: 896,
  large: 1440,
};

export const contentColumnsCount: Record<BreakpointName, number> = {
  small: 4,
  medium: 8,
  large: 12,
};

/**
 * Return the max width for the given breakpoint and the given number of
 * columns, excluding the outer gaps on either side.
 */
export const griddedColumnsMaxInnerWidth = (breakpoint: BreakpointName, columnsCount: number) => {
  const columnWidth =
    (maxContentWidth[breakpoint] -
      (outerGapWidth[breakpoint] * 2 +
        (contentColumnsCount[breakpoint] - 1) * innerGapWidth[breakpoint])) /
    contentColumnsCount[breakpoint];

  return columnWidth * columnsCount + innerGapWidth[breakpoint] * (columnsCount - 1);
};

export const responsiveStyles = ({
  medium,
  large,
}: {
  medium?: StyleRule;
  large?: StyleRule;
}): MediaQueries<StyleRule> =>
  !!medium || !!large
    ? {
        '@media': {
          ...(medium ? {[`screen and (min-width: ${screenSizes.medium}px)`]: medium} : undefined),
          ...(large ? {[`screen and (min-width: ${screenSizes.large}px)`]: large} : undefined),
        },
      }
    : {};

/**
 * Given a column count at different breakpoints build a set of style rules and media queries
 * such that the content you're styling is sized correctly to the grid columns you provided.
 *
 * Each breakpoint is optional so you can pick and choose which breakpoints you'd like to style.
 */
export const responsiveGridSizing = (
  gridColumns: Partial<Record<BreakpointName, number>>,
  /**
   * When `true` the element will get `marginLeft: auto; marginRight: auto` applied to center itself.
   *
   * @default true
   */
  center: boolean = true,
) => {
  // We always want the content to be centered veritcally
  // Explicitly apply the marginLeft/Right instead of using sprinkles to prevent circular imports
  const styles: ComplexStyleRule = [];

  if (center) {
    styles.push({marginLeft: 'auto', marginRight: 'auto'});
  }

  // Construct an object with the same type as the arg passed to `responsiveStyles`.
  // We construct this object and then pass the object to a single `responsiveStyles()` call
  // to ensure specificity is correct (large should override medium)
  const mediaQueries: Parameters<typeof responsiveStyles>[0] = {};

  // Loop over each breakpoint and build the styles for any provided `gridColumns`
  (['small', 'medium', 'large'] as BreakpointName[]).forEach(size => {
    const columnCount = gridColumns[size];

    if (columnCount) {
      // Make sure our column count doesn't extend past the grid
      const adjusted = Math.min(columnCount, contentColumnsCount[size]);

      const style = {
        // Set the width so that the content tries to size itself all the way to the edge
        // of the browser window...
        width: `calc(100% - (${outerGapWidth[size]}px * 2))`,
        //... But restrict the size so that it maxes out at number of columns provided
        maxWidth: griddedColumnsMaxInnerWidth(size, adjusted),
      };

      // Small is the default size so push that as a StyleRule
      if (size === 'small') {
        styles.push(style);
      } else {
        mediaQueries[size] = style;
      }
    }
  });

  styles.push(responsiveStyles(mediaQueries));

  return styles;
};
